import { DirectoryEntityType, DirectoryOtherEstablishmentSearchDto } from "@/ContactDirectory/Api/medfar-contactdirectory/dist";
import { PhoneNumberHelper } from "@/ContactDirectory/Helpers/PhoneNumberHelper";
import { DirectorySearchHelper } from "../Helpers/DirectorySearchHelper";
import { DirectoryAddress } from "./DirectoryAddress";
import { DirectoryFaxNumber } from "./DirectoryFaxNumber";
import { DirectoryPhoneNumber } from "./DirectoryPhoneNumber";
import { IDirectoryEntity } from "./IDirectoryEntity";
import { IGetDisplayName } from "./IGetDisplayName";

export class DirectoryOtherEstablishment implements IGetDisplayName, IDirectoryEntity {
    public type: DirectoryEntityType;
    public id: string;
    public previousRevisionId?: number;
    public name: string;
    public address: DirectoryAddress;
    public faxNumbers: DirectoryFaxNumber[];
    public phoneNumbers: DirectoryPhoneNumber[];
    public email: string;
    public responsiblePersonName: string;
    public note: string;

    public isApproved?: boolean;
    public isSuggestion?: boolean;

    public orderNumber?: number;
    public selectedLinkedFaxNumber?: DirectoryFaxNumber;

    /** Identifiers of fax numbers that match any search text sent. (description and/or fax number) */
    public matchingFaxNumberIds: string[];

    public constructor(init?: Partial<DirectoryOtherEstablishment>) {
        Object.assign(this, init);
        this.address = new DirectoryAddress(init?.address);
        this.faxNumbers = init?.faxNumbers?.map(p => new DirectoryFaxNumber(p)) ?? [];
        this.phoneNumbers = init?.phoneNumbers?.map(p => new DirectoryPhoneNumber(p)) ?? [];
        this.type = DirectoryEntityType.Other;
    }

    public getDefaultFaxNumberToSelect(): DirectoryFaxNumber | null {
        if (this.matchingFaxNumberIds.length === 1) {
            return this.faxNumbers.firstOrDefault(p => p.id == this.matchingFaxNumberIds[0]);
        }

        return this.faxNumbers
            .filter(p => !this.matchingFaxNumberIds.any() || this.matchingFaxNumberIds.includes(p.id))
            .firstOrDefault();
    }

    public getRawFaxNumber(): string {
        return this.faxNumbers?.firstOrDefault() ? this.faxNumbers?.firstOrDefault().number : null;
    }

    public getRawPhoneNumber(): string {
        return this.phoneNumbers?.firstOrDefault() ? this.phoneNumbers?.firstOrDefault().number : null;
    }

    public getDirectoryAddressForDisplay(): string | null {
        return DirectorySearchHelper.formatAddress(this.address);
    }

    public getFaxNumbersForDisplay(): { formattedNumber: string, description?: string }[] {
        const addressCountryId = this.address.countryId;
        const addressStateId = this.address.stateId;

        return this.faxNumbers
            .filter(p => !this.matchingFaxNumberIds.any() || this.matchingFaxNumberIds.includes(p.id))
            .map(fax => ({
                formattedNumber: PhoneNumberHelper
                    .format(
                        fax?.number,
                        fax?.countryId ?? addressCountryId,
                        cacheStore
                            .GetGeographicsStatesForCountryId(fax?.countryId ?? addressCountryId)
                            ?.some(p => p.Id === addressStateId) ? addressStateId : null
                    ),
                description: fax.description
            }));
    }

    public hasFaxNumbers(): boolean {
        return this.faxNumbers.length > 0;
    }

    public hasPhoneNumbers(): boolean {
        return this.phoneNumbers.length > 0;
    }

    public shouldRenderMultipleFaxNumbers(): boolean {
        if (this.matchingFaxNumberIds.length === 1 || this.faxNumbers.length === 1)
            return false;

        return this.matchingFaxNumberIds.length >= 0 && this.faxNumbers.length > 1;
    }

    public getFaxNumbersModels(): DirectoryFaxNumber[] {
        return this.faxNumbers;
    }

    public getPhoneNumbersForDisplay(): string[] {
        const addressCountryId = this.address.countryId;
        const addressStateId = this.address.stateId;

        return this.phoneNumbers.map(phone => PhoneNumberHelper
            .format(
                phone?.number,
                phone?.countryId ?? addressCountryId,
                cacheStore
                    .GetGeographicsStatesForCountryId(phone?.countryId ?? addressCountryId)
                    ?.some(p => p.Id === addressStateId) ? addressStateId : null,
                phone?.extension
            ));
    }

    public getLinkedDirectoryEntities(): IDirectoryEntity[] {
        return [];
    }

    public static fromSearchDto(dto: DirectoryOtherEstablishmentSearchDto, orderNumber?: number): DirectoryOtherEstablishment {
        if (!dto)
            return null;

        return new DirectoryOtherEstablishment({
            ...dto,
            address: DirectoryAddress.fromSearchDto(dto.address),
            faxNumbers: dto?.faxNumbers?.map(p => DirectoryFaxNumber.fromSearchDto(p)),
            phoneNumbers: dto?.phoneNumbers?.map(p => DirectoryPhoneNumber.fromSearchDto(p)),
            orderNumber
        });
    }

    public getDisplayName(): string {
        return this.name;
    }
}
